import { useEffect, useRef, useState } from 'react';

import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';

import { Button } from '@karehero/llama/components/atoms/Button/Button';
import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

import Layout from '@/components/layout/Layout';
import { inFrameDialogProps } from '@/designs-dev/device/inFrameDialog';
import CamShell, { type CamNavItem } from '@/cam-admin/CamShell';
import { CAM_COMPACT } from '@/cam-admin/responsive';

import CareSearch, {
  type CareSearchInitial,
} from '@/cam-admin/care-search/CareSearch';
import ActionsTab from '@/cam-admin/control-panel/ActionsTab';
import CallsTab, { type CallsIntent } from '@/cam-admin/control-panel/CallsTab';
import DocumentsTab from '@/cam-admin/control-panel/DocumentsTab';
import FormsTab from '@/cam-admin/control-panel/FormsTab';
import OverviewTab from '@/cam-admin/control-panel/OverviewTab';
import ActionDrawer from '@/cam-admin/control-panel/ActionDrawer';
import CallDrawer from '@/cam-admin/control-panel/CallDrawer';
import CarerDrawer from '@/cam-admin/control-panel/CarerDrawer';
import ProfileDrawer from '@/cam-admin/control-panel/ProfileDrawer';
import FormDrawer from '@/cam-admin/control-panel/FormDrawer';
import LogCallDrawer, {
  type CallNoteFields,
} from '@/cam-admin/control-panel/LogCallDrawer';
import LinkedActionModal from '@/cam-admin/control-panel/LinkedActionModal';
import {
  CARE_CIRCLE,
  SEED_ACTIONS,
  SEED_CALLS,
  SEED_DOCUMENTS,
  SEED_FORMS,
  firstName,
  todayLabel,
  type ActionItem,
  type Call,
  type DocumentItem,
} from '@/cam-admin/control-panel/fixtures';
import { ShellOverlay } from '@/cam-admin/control-panel/bits';
import NotificationsPage from '@/cam-admin/notifications/NotificationsPage';
import {
  CAM_NOTIFICATIONS,
  type CamNotification,
} from '@/cam-admin/notifications/Notifications.fixtures';
import ShortlistTab from '@/cam-admin/paywall/ShortlistTab';
import RowDrawer from '@/cam-admin/paywall/RowDrawer';
import PublishAndSend from '@/cam-admin/paywall/PublishAndSend';
import PublishHomes from '@/cam-admin/paywall/PublishHomes';
import SendPreview from '@/cam-admin/paywall/SendPreview';
import ShortlistModal from '@/components/mui/ShortlistModal';
import {
  useShortlists,
  type ShortlistInitial,
} from '@/cam-admin/paywall/useShortlists';

export type TabId =
  | 'overview'
  | 'actions'
  | 'calls'
  | 'documents'
  | 'forms'
  | 'care-search'
  | 'shortlist';

const TABS: { id: TabId; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'actions', label: 'Actions' },
  { id: 'calls', label: 'Calls' },
  { id: 'documents', label: 'Documents' },
  { id: 'forms', label: 'Forms' },
  { id: 'care-search', label: 'Care search' },
];

// The paywall shortlist, opt-in rather than a standard tab. The paywall is still
// an open question, so it should not appear on the descoping build or in the
// handover specs until it is decided — the paywall prototype passes
// `withShortlist` and nothing else has to change.
const SHORTLIST_TAB: { id: TabId; label: string } = {
  id: 'shortlist',
  // Plural: a circle has several, and the tab switches between them.
  label: 'Shortlists',
};

// Harness-only: seed the notifications feed into one of its states (see the
// Notifications page's states — full feed, all read, or empty).
export type NotificationsInitial = {
  empty?: boolean;
  allRead?: boolean;
};

const seedNotifications = (
  initial?: NotificationsInitial,
): CamNotification[] => {
  if (initial?.empty) return [];
  if (initial?.allRead) {
    return CAM_NOTIFICATIONS.map((n) => ({ ...n, read: true }));
  }
  return CAM_NOTIFICATIONS;
};

/**
 * The care recipient control panel: the CAM's single workspace for one care
 * circle. Owns the chrome (app bar, nav rail, header, tabs) and the data the
 * tabs share, so actions created from a call show up in the actions list and
 * vice versa. Care search keeps all of its own state inside CareSearch.
 */
const ControlPanel = ({
  initialTab = 'care-search',
  intake,
  initialView = 'panel',
  embedded = false,
  careSearch,
  documentsInitial,
  callsInitial,
  notificationsInitial,
  onNavigate,
  nav,
  activeNav = 'Care circles',
  backPath = '/care-circles',
  tabLabels,
  descopedCallNotes = false,
  withShortlist = false,
  shortlist,
}: {
  initialTab?: TabId;
  /**
   * Whether a Tell Us Once intake sits behind this circle. Passed through to the
   * Overview tab, which is the only place it shows. Opt-in like `withShortlist`:
   * the intake is still being designed, so the CAM Admin pages render without it
   * and the Tell Us Once flows page passes it.
   */
  intake?: 'consented' | 'declined';
  // Open straight onto the notifications feed (the Notifications menu entry).
  initialView?: 'panel' | 'notifications';
  embedded?: boolean;
  // Injected by the stage so the shell's nav rail can route (e.g. back to the
  // care circles index) without the panel itself depending on the router.
  onNavigate?: (path: string) => void;
  // Override the CAM nav rail / active item / back target — the descoping build
  // passes its own rail so opening a circle keeps you inside that prototype.
  nav?: CamNavItem[];
  activeNav?: string;
  backPath?: string;
  // Override tab labels per id — the descoping build renames Calls to Call notes.
  tabLabels?: Partial<Record<TabId, string>>;
  // Show the paywall Shortlist tab. Off by default: the paywall is undecided, so
  // only the prototype exploring it asks for the tab.
  withShortlist?: boolean;
  // Which state the shortlist starts in, when the tab is shown.
  shortlist?: ShortlistInitial;
  // Descoping call notes: a Create call note button, one flat list (no
  // Upcoming/Past), and a notes-only detail drawer.
  descopedCallNotes?: boolean;
  // Pass-through jump-state for the tabs (handover minis land on a tab and
  // open a modal / detail straight away).
  careSearch?: CareSearchInitial;
  documentsInitial?: { upload?: boolean };
  callsInitial?: CallsIntent;
  // Pass-through jump-state for the notifications feed.
  notificationsInitial?: NotificationsInitial;
}) => {
  const [tab, setTab] = useState<TabId>(initialTab);
  const tabs = withShortlist ? [...TABS, SHORTLIST_TAB] : TABS;
  // The shortlist state lives here because its two surfaces sit in two places:
  // the table in the tab body, the row drawer in the overlays slot below, where
  // it can cover the whole workspace. Called unconditionally — hooks must be —
  // and simply unused when the tab is off.
  const shortlists = useShortlists(shortlist ?? {});
  // Either the care-circle workspace or the global notifications feed. The
  // bell opens notifications inside the same chrome (app bar + nav rail).
  const [view, setView] = useState<'panel' | 'notifications'>(initialView);
  // The menu renders the same stage instance for both /care-search and
  // /care-search/notifications, so React keeps this mounted and only the prop
  // changes — follow it so the menu entries actually switch the view.
  useEffect(() => {
    setView(initialView);
  }, [initialView]);
  // The care recipient whose control panel is on screen. Switches when a
  // notification is opened, so the deep-link lands on the right person's page.
  const [customer, setCustomer] = useState('Margarette Qualley');
  const [notifications, setNotifications] = useState<CamNotification[]>(() =>
    seedNotifications(notificationsInitial),
  );
  const unreadCount = notifications.filter((n) => !n.read).length;
  // Bumped whenever navigation carries an intent, so the target tab remounts
  // and picks it up.
  const [epoch, setEpoch] = useState(0);
  const [callsIntent, setCallsIntent] = useState<CallsIntent | undefined>(
    callsInitial,
  );
  const [docsUpload, setDocsUpload] = useState(
    Boolean(documentsInitial?.upload),
  );
  const [toast, setToast] = useState<string | null>(null);

  // Which carer's details are open, by name. null = closed.
  const [openCarer, setOpenCarer] = useState<string | null>(null);
  // The recipient's record, open for editing from the Overview card's Edit.
  const [editingProfile, setEditingProfile] = useState(false);

  // The forms and whichever one is open. Up here with the actions, the calls
  // and the documents, because the drawer that edits them is rendered up here
  // too: a drawer inside a tab body would be clipped by it.
  const [forms, setForms] = useState(() => structuredClone(SEED_FORMS));
  const [openForm, setOpenForm] = useState<string | null>(null);

  const setFormAnswer = (questionId: string, value: string) =>
    setForms((prev) =>
      prev.map((f) =>
        f.id !== openForm
          ? f
          : {
              ...f,
              sections: f.sections.map((s) => ({
                ...s,
                questions: s.questions.map((q) =>
                  q.id === questionId ? { ...q, answer: value } : q,
                ),
              })),
            },
      ),
    );

  // A row saved in one of the fact drawers. The drawer stays open, so the toast
  // is the only thing that confirms the press did anything.
  const saveFacts = () => setToast('Change saved');

  // Shared data, so cross-tab links stay consistent.
  const [actions, setActions] = useState<ActionItem[]>(SEED_ACTIONS);
  const [calls, setCalls] = useState<Call[]>(SEED_CALLS);
  const [documents, setDocuments] = useState<DocumentItem[]>(SEED_DOCUMENTS);

  // The create-call-note drawer (descoping build). Prepends a new call on save,
  // mirroring the inline log-a-call flow.
  const [loggingCall, setLoggingCall] = useState(false);
  const addCallNote = (f: CallNoteFields) => {
    setCalls([
      {
        id: `new-c${calls.length + 1}`,
        title: f.title || 'Call note',
        withWho: 'You',
        date: todayLabel(),
        time: '',
        notes: f.notes,
      },
      ...calls,
    ]);
    setToast('Call note created');
    setLoggingCall(false);
  };

  // View / create / edit an action, in the ActionDrawer over the frame. null =
  // closed. The drawer keeps rendering its last content while it slides shut, so
  // it doesn't flip to an empty form mid-animation.
  const [actionForm, setActionForm] = useState<{
    mode: 'view' | 'create' | 'edit';
    id?: string;
  } | null>(null);
  const lastForm = useRef(actionForm);
  if (actionForm) lastForm.current = actionForm;
  const shownForm = actionForm ?? lastForm.current;
  const shownAction = shownForm?.id
    ? actions.find((a) => a.id === shownForm.id)
    : undefined;
  const idRef = useState({ n: 0 })[0];

  // The action queued for a series-aware delete confirmation.
  const [deleting, setDeleting] = useState<ActionItem | null>(null);

  const createAction = (fields: Partial<ActionItem>) => {
    const id = `new-a${(idRef.n += 1)}`;
    setActions([
      ...actions,
      {
        id,
        title: fields.title ?? 'Untitled action',
        createdBy: 'you',
        ...fields,
      },
    ]);
    setToast(
      fields.assignee
        ? `Action created. ${firstName(fields.assignee)} has been notified`
        : 'Action created',
    );
    setActionForm(null);
  };

  const saveAction = (id: string, fields: Partial<ActionItem>) => {
    const before = actions.find((a) => a.id === id);
    setActions(actions.map((a) => (a.id === id ? { ...a, ...fields } : a)));
    const notify = fields.assignee ?? before?.assignee;
    setToast(
      notify
        ? `Action saved. ${firstName(notify)} has been notified`
        : 'Action saved',
    );
    setActionForm(null);
  };

  const toggleActionDone = (id: string) => {
    const target = actions.find((a) => a.id === id);
    setActions(actions.map((a) => (a.id === id ? { ...a, done: !a.done } : a)));
    if (target && !target.done && target.assignee) {
      setToast(
        `Marked as done. ${firstName(target.assignee)} has been notified`,
      );
    }
  };

  const deleteAction = () => {
    if (!deleting) return;
    setActions(actions.filter((a) => a.id !== deleting.id));
    setToast(
      deleting.repeats
        ? 'Recurring action deleted, including all future occurrences'
        : 'Action deleted',
    );
    setDeleting(null);
    setActionForm(null);
  };

  // The call detail drawer (view + inline note edit). null = closed; kept stable
  // during the slide-out via lastCall.
  const [callDetail, setCallDetail] = useState<{
    id: string;
    editing: boolean;
  } | null>(
    callsInitial?.kind === 'open'
      ? { id: callsInitial.id, editing: false }
      : null,
  );
  const lastCall = useRef(callDetail);
  if (callDetail) lastCall.current = callDetail;
  const shownCall = callDetail ?? lastCall.current;
  const shownCallData = shownCall
    ? calls.find((c) => c.id === shownCall.id)
    : undefined;
  // The call whose Linked actions section is creating an action (in a modal).
  const [creatingLinked, setCreatingLinked] = useState<string | null>(null);

  const openCall = (id: string) => {
    setActionForm(null);
    go('calls');
    setCallDetail({ id, editing: false });
  };

  // The call note queued for delete confirmation (descoping build).
  const [deletingCall, setDeletingCall] = useState<string | null>(null);
  const deleteCall = () => {
    if (!deletingCall) return;
    setCalls(calls.filter((c) => c.id !== deletingCall));
    setToast('Call note deleted');
    setDeletingCall(null);
    setCallDetail(null);
  };

  const saveCallNotes = (id: string, text: string) => {
    setCalls(calls.map((c) => (c.id === id ? { ...c, notes: text } : c)));
    setToast('Call summary saved. Sophie has been notified');
    setCallDetail({ id, editing: false });
  };

  const addCallDocument = (id: string) => {
    const name = `example_file_${(idRef.n += 1)}.pdf`;
    setCalls(
      calls.map((c) =>
        c.id === id ? { ...c, documents: [...(c.documents ?? []), name] } : c,
      ),
    );
    setToast('Document linked to this call');
  };

  const removeCallDocument = (id: string, index: number) => {
    setCalls(
      calls.map((c) =>
        c.id === id
          ? {
              ...c,
              documents: (c.documents ?? []).filter((_, j) => j !== index),
            }
          : c,
      ),
    );
    setToast('Document removed from this call');
  };

  const createLinkedAction = (callId: string, fields: Partial<ActionItem>) => {
    const id = `new-a${(idRef.n += 1)}`;
    setActions([
      ...actions,
      {
        id,
        title: fields.title ?? 'Untitled action',
        createdBy: 'you',
        linkedCallId: callId,
        callPhase: 'after',
        ...fields,
      },
    ]);
    setToast(
      fields.assignee
        ? `Action created. ${firstName(fields.assignee)} has been notified`
        : 'Action created',
    );
    setCreatingLinked(null);
  };

  const go = (next: TabId, intent?: () => void) => {
    setCallsIntent(undefined);
    setDocsUpload(false);
    intent?.();
    setTab(next);
    setEpoch((e) => e + 1);
  };

  return (
    <CamShell
      activeNav={activeNav}
      nav={nav}
      onNavigate={onNavigate}
      bellBadge={unreadCount}
      onBell={() => setView('notifications')}
      overlays={
        <>
          {/* The shortlist row drawer: provider detail and the CAM's note. Here
              rather than inside the tab so it covers the whole panel, the same
              way the action and call drawers do. */}
          {withShortlist && (
            <RowDrawer
              provider={shortlists.openProvider}
              isPublished={shortlists.isPublished}
              isPaywall={shortlists.isPaywall}
              onNote={shortlists.setNote}
              onCamAnswers={shortlists.setCamAnswers}
              onMakeVisible={shortlists.makeVisible}
              onClose={shortlists.closeRowDrawer}
            />
          )}
          {/* Publish and send, out here for the same reason: it acts on the whole
              list, and `inFrame` is only known at the shell. */}
          {withShortlist && (
            <PublishAndSend shortlists={shortlists} inFrame={embedded} />
          )}
          {/* And the care home half of the same act: no words to read, one
              consequence to. Its own component rather than a mode of the modal
              above, because the two share the button slot and nothing else. */}
          {withShortlist && (
            <PublishHomes shortlists={shortlists} inFrame={embedded} />
          )}
          {/* The preview of what is about to go out. A sibling rather than a child
              of the modal: it covers the workspace like the row drawer does, and a
              dialog inside a dialog would leave a CAM two Cancels deep. */}
          {withShortlist && <SendPreview shortlists={shortlists} />}
          {/* And the shortlist modal — the same one Care search opens, reached
              from the foot of the selector (create) or the link beside it (edit).
              Keyed on the mode so the name field seeds fresh each time, and
              mounted only while open for the same reason. */}
          {withShortlist && shortlists.dialog !== null && (
            <ShortlistModal
              key={shortlists.dialog}
              open
              mode={shortlists.dialog}
              initialName={
                shortlists.dialog === 'edit'
                  ? shortlists.current.name
                  : shortlists.nextListName
              }
              inFrame={embedded}
              onClose={shortlists.closeDialog}
              onSubmit={
                shortlists.dialog === 'edit'
                  ? shortlists.renameList
                  : shortlists.createList
              }
            />
          )}
          {/* notifications: a full-bleed overlay over the whole control panel —
              a global feed you step into from the bell, not a page within the
              per-customer workspace. Back drops you exactly where you were. */}
          {view === 'notifications' && (
            <ShellOverlay onBack={() => setView('panel')}>
              <NotificationsPage
                items={notifications}
                onChange={setNotifications}
                onOpen={(n) => {
                  // Deep-link: drop the overlay and land on that care
                  // recipient's control panel at the tab the activity belongs
                  // to.
                  setCustomer(n.customer);
                  setView('panel');
                  go(n.target);
                }}
              />
            </ShellOverlay>
          )}

          {/* a carer's details — a right drawer over the frame, so the rail's
              rows can be names and nothing else */}
          <CarerDrawer
            open={Boolean(openCarer)}
            carer={CARE_CIRCLE.carers.find((c) => c.name === openCarer)}
            onSave={saveFacts}
            onClose={() => setOpenCarer(null)}
          />

          {/* the recipient's record, editable here as well as on the member's
              About tab — the same FactList over the same groups */}
          <ProfileDrawer
            open={editingProfile}
            onSave={saveFacts}
            onClose={() => setEditingProfile(false)}
          />

          {/* a form, over the workspace rather than swallowing the Forms tab */}
          <FormDrawer
            open={Boolean(openForm)}
            form={forms.find((f) => f.id === openForm)}
            onSaveAnswer={(id, value) => {
              setFormAnswer(id, value);
              setToast('Answer updated');
            }}
            onDownload={() =>
              setToast('Filling the form is mocked in the prototype')
            }
            onClose={() => setOpenForm(null)}
          />

          {/* view / create / edit an action — a right drawer over the frame */}
          <ActionDrawer
            open={Boolean(actionForm)}
            mode={shownForm?.mode ?? 'create'}
            action={shownAction}
            linkedCall={calls.find((c) => c.id === shownAction?.linkedCallId)}
            onClose={() => setActionForm(null)}
            onSubmit={(fields) => {
              if (shownForm?.mode === 'edit' && shownForm.id) {
                saveAction(shownForm.id, fields);
              } else {
                createAction(fields);
              }
            }}
            onEdit={() =>
              shownForm?.id && setActionForm({ mode: 'edit', id: shownForm.id })
            }
            onDelete={() => shownAction && setDeleting(shownAction)}
            onToggleDone={() => shownForm?.id && toggleActionDone(shownForm.id)}
            onOpenCall={openCall}
          />

          {/* call detail — a right drawer over the frame, same pattern */}
          <CallDrawer
            open={Boolean(callDetail)}
            call={shownCallData}
            linkedActions={actions.filter(
              (a) => a.linkedCallId === shownCall?.id,
            )}
            documents={shownCallData?.documents ?? []}
            editing={shownCall?.editing ?? false}
            onClose={() => setCallDetail(null)}
            onEdit={() =>
              shownCall && setCallDetail({ id: shownCall.id, editing: true })
            }
            onSaveNotes={(text) =>
              shownCall && saveCallNotes(shownCall.id, text)
            }
            onDiscardEdit={() =>
              shownCall && setCallDetail({ id: shownCall.id, editing: false })
            }
            onToggleActionDone={toggleActionDone}
            onOpenAction={(id) => {
              setCallDetail(null);
              setActionForm({ mode: 'view', id });
            }}
            onCreateLinkedAction={() =>
              shownCall && setCreatingLinked(shownCall.id)
            }
            onAddDocument={() => shownCall && addCallDocument(shownCall.id)}
            onRemoveDocument={(i) =>
              shownCall && removeCallDocument(shownCall.id, i)
            }
            onDelete={
              descopedCallNotes
                ? () => shownCall && setDeletingCall(shownCall.id)
                : undefined
            }
            notesOnly={descopedCallNotes}
          />

          {/* create a call note — a right drawer over the frame (descoping) */}
          <LogCallDrawer
            open={loggingCall}
            onClose={() => setLoggingCall(false)}
            onSubmit={addCallNote}
          />

          {creatingLinked && shownCallData && (
            <LinkedActionModal
              call={shownCallData}
              inFrame={embedded}
              onCreate={(fields) => createLinkedAction(creatingLinked, fields)}
              onClose={() => setCreatingLinked(null)}
            />
          )}

          {/* delete confirm — series-aware copy per the spec */}
          <Dialog
            open={Boolean(deleting)}
            onClose={() => setDeleting(null)}
            maxWidth='xs'
            fullWidth
            {...(embedded ? inFrameDialogProps : {})}
          >
            <DialogTitle>
              <Stack
                sx={{ alignItems: 'center', justifyContent: 'space-between' }}
                direction='row'
              >
                <Typography variant='h3' color='primary.main'>
                  Delete action
                </Typography>
                <IconButton
                  aria-label='close'
                  onClick={() => setDeleting(null)}
                >
                  <Icon icon='xmark' fontSize={18} />
                </IconButton>
              </Stack>
            </DialogTitle>

            <DialogContent>
              <Typography variant='subtitle1'>
                {deleting?.repeats
                  ? 'Are you sure you want to delete this recurring action? This will delete all future occurrences and can’t be undone.'
                  : 'Are you sure you want to delete this action? This can’t be undone.'}
              </Typography>
            </DialogContent>

            <DialogActions>
              <Button
                variant='secondary'
                ariaLabel='Cancel'
                onPress={() => setDeleting(null)}
              >
                Cancel
              </Button>
              <Button
                variant='primary'
                ariaLabel='Delete action'
                onPress={deleteAction}
              >
                Delete action
              </Button>
            </DialogActions>
          </Dialog>

          {/* delete confirm — call note (descoping) */}
          <Dialog
            open={Boolean(deletingCall)}
            onClose={() => setDeletingCall(null)}
            maxWidth='xs'
            fullWidth
            {...(embedded ? inFrameDialogProps : {})}
          >
            <DialogTitle>
              <Stack
                sx={{ alignItems: 'center', justifyContent: 'space-between' }}
                direction='row'
              >
                <Typography variant='h3' color='primary.main'>
                  Delete call note
                </Typography>
                <IconButton
                  aria-label='close'
                  onClick={() => setDeletingCall(null)}
                >
                  <Icon icon='xmark' fontSize={18} />
                </IconButton>
              </Stack>
            </DialogTitle>

            <DialogContent>
              <Typography variant='subtitle1'>
                Are you sure you want to delete this call note? This can’t be
                undone.
              </Typography>
            </DialogContent>

            <DialogActions>
              <Button
                variant='secondary'
                ariaLabel='Cancel'
                onPress={() => setDeletingCall(null)}
              >
                Cancel
              </Button>
              <Button
                variant='primary'
                ariaLabel='Delete call note'
                onPress={deleteCall}
              >
                Delete call note
              </Button>
            </DialogActions>
          </Dialog>

          <Snackbar
            open={Boolean(toast)}
            autoHideDuration={3000}
            onClose={() => setToast(null)}
            message={toast}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          />
        </>
      }
    >
      <Layout sx={{ [CAM_COMPACT]: { p: 2 } }}>
        <Stack sx={{ alignItems: 'center', mb: 1 }} direction='row' spacing={1}>
          <IconButton
            edge='start'
            aria-label='Back to care circles'
            onClick={() => onNavigate?.(backPath)}
          >
            <Icon icon='chevron-left' fontSize={18} />
          </IconButton>
          <Typography variant='h4'>{customer}’s care circle</Typography>
        </Stack>

        {/* MUI's sliding indicator measures in visual pixels, which lands
              under the wrong tab inside the scaled laptop frame — use a CSS
              underline on the selected tab instead */}
        <Tabs
          slotProps={{ indicator: { sx: { display: 'none' } } }}
          // Clamped: an initialTab that isn't in this build's tab set (asking for
          // 'shortlist' without withShortlist) would otherwise select nothing.
          value={Math.max(
            0,
            tabs.findIndex((t) => t.id === tab),
          )}
          onChange={(_, i) => go(tabs[i].id)}
          variant='scrollable'
          scrollButtons={false}
        >
          {tabs.map((t) => (
            <Tab
              key={t.id}
              label={tabLabels?.[t.id] ?? t.label}
              sx={{
                '&.Mui-selected': {
                  boxShadow: (theme) =>
                    `inset 0 -2px 0 ${theme.palette.primary.main}`,
                },
              }}
            />
          ))}
        </Tabs>
        <Divider />

        {/* tab content. Care search manages its own two-pane scrolling;
              every other tab scrolls as a simple page. */}
        {tab === 'care-search' ? (
          // No top margin here: CareSearch supplies its own 24px above the
          // search field, matching the pt={3} the other tabs get below.
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <CareSearch embedded={embedded} initial={careSearch} />
          </Box>
        ) : (
          <Box
            sx={{ flex: 1, minHeight: 0, overflow: 'auto', pt: 3, pr: 1 }}
            key={`${tab}-${epoch}`}
          >
            {tab === 'overview' && (
              <OverviewTab
                onToast={setToast}
                intake={intake}
                onOpenCarer={setOpenCarer}
                onViewCalls={() => go('calls')}
                onOpenCall={openCall}
                onEditProfile={() => setEditingProfile(true)}
              />
            )}
            {tab === 'actions' && (
              <ActionsTab
                actions={actions}
                onCreate={() => setActionForm({ mode: 'create' })}
                onView={(id) => setActionForm({ mode: 'view', id })}
                onToggleDone={toggleActionDone}
              />
            )}
            {tab === 'calls' && (
              <CallsTab
                calls={calls}
                intent={callsIntent}
                onChangeCalls={setCalls}
                onToast={setToast}
                onOpenCall={openCall}
                onCreate={
                  descopedCallNotes ? () => setLoggingCall(true) : undefined
                }
                flat={descopedCallNotes}
              />
            )}
            {tab === 'documents' && (
              <DocumentsTab
                documents={documents}
                embedded={embedded}
                initialUpload={docsUpload}
                onChange={setDocuments}
                onToast={setToast}
              />
            )}
            {tab === 'forms' && <FormsTab forms={forms} onOpen={setOpenForm} />}
            {tab === 'shortlist' && withShortlist && (
              <ShortlistTab shortlists={shortlists} />
            )}
          </Box>
        )}
      </Layout>
    </CamShell>
  );
};

export default ControlPanel;
